import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import {
  Box,
  Container,
  LinearProgress,
  MenuItem,
  Modal, 
  TextField, 
  Typography
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PublicIcon from '@mui/icons-material/Public';

import ModalTopBar from '../../../components/topBar/ModalTopBar';
import LoadingButton from '../../../components/buttons/Button';
import useStepperInit from '../../../components/hooks/useStepperInit';
import { stateList } from '../../../components/utils/usStateCodeList';

import { 
  GetCompanies,
  CreateCompany, 
  UpdateCompanyConfig
} from '../../../redux/actions/dashboard/companiesActions';

import './styles.scss';

const taxIdTypeList = [
  { label: "EIN", value: "ein" },
  { label: "TIN", value: "tin" }
];

const entityTypeList = [
  { label: "Sole proprietorship", value: "sole_proprietorship" },
  { label: "Corporation", value: "corporation" },
  { label: "LLC", value: "llc" },
  { label: "Partnership, LP's, LLP's", value: "partnership" },
  { label: "Non-profit", value: "non_profit" },
  { label: "Trust", value: "trust" },
  { label: "Unincorporated association", value: "unincorporated_association" },
  { label: "Publicly traded corporation", value: "publicy_traded_corporation" },
  { label: "Nominee or bearer shares company", value: "nominee_or_bearer_shares_company" },
];

const Data = {
  LegalName: "",
  TradingName: "",
  EntityType: "",
  Address: "",
  City: "",
  State: "",
  Country: "US",
  Pincode: "",
  TaxIdType: "",
  TaxId: "",
  IncorporationDate: null,
  ContactFirstName: "",
  ContactLastName: "",
  Email: "",
  ContactPersonDOB: null,
  CompanyWebsite: "",
  EstimateAnnualRevenue: "",
  Industry: "",
  DomesticRail: false,
  GlobalRail: false,
};

const CompaniesModal = ({
  open, 
  setOpen, 
  update
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(-1);

  const [companyId, setCompanyId] = useState(null);

  const [
    // eslint-disable-next-line no-unused-vars
    steps,
    activeStep, 
    handleReset, 
    handleNext,
    // eslint-disable-next-line no-unused-vars
    handleBack
  ] = useStepperInit(['Company details', 'Industry', 'Payment rails']);
    
  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(-1);
    handleReset();
  };

  useEffect(() => {
    if(open){
      handleReset();
      if(update.defaultMode === 1){
        // mode = 1 - create company
        setMode(1);
      } else if (update.defaultMode === 2){

      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (e) => {
    if(e.target.name === "Pincode") {
      setFormData({ ...formData, [e.target.name]: e.target.value.slice(0, 5) });
    } else if(e.target.name === "TaxId") {
      setFormData({ ...formData, [e.target.name]: e.target.value.slice(0, 9) });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const isStep0Valid = () => {
    const isDateOfBirthRequired = 
      formData.EntityType === "sole_proprietorship" ||
      formData.EntityType === "unincorporated_association" || 
      formData.EntityType === "trust";
    
    return (
      formData.LegalName !== "" &&
      formData.EntityType !== "" &&
      formData.ContactFirstName !== "" &&
      formData.ContactLastName !== "" &&
      formData.Email !== "" &&
      formData.Address !== "" &&
      formData.City !== "" &&
      formData.State !== "" &&
      formData.Pincode !== "" &&
      formData.TaxIdType !== "" &&
      formData.TaxId !== "" &&
      formData.IncorporationDate !== null &&
      (!isDateOfBirthRequired || formData.ContactPersonDOB !== null)
    );
  };

  const handleSubmit = async () => {
    try {
      if(mode === 1){
        // mode = 1 - create company
        if (activeStep === 0){
          // create company
          let obj = {
            "legal_name": formData.LegalName || "",
            "address": formData.Address || "",
            "city": formData.City || "",
            "state": formData.State || "",
            "country": formData.Country || "",
            "pincode": formData.Pincode || "",
            "tax_id": formData.TaxId || "",
            "tax_id_type": formData.TaxIdType || "",
            "incorporation_date": dayjs(formData.IncorporationDate).format("YYYY-MM-DD") || "",
            "entity_type": formData.EntityType || "",
            "contact_name": formData.ContactName || "",
            "email": formData.Email || "",
          };

          dispatch(CreateCompany(obj, setLoading)).then(({ res, statusCode }) => {
            if (statusCode === 201){
              setCompanyId(res.data.Id);
              dispatch(GetCompanies(1, 500, "", false, setLoading)).then(() => {
                handleNext();
              });
            } else {
              handleClose();
            }
          });
        } else if (activeStep === 1){
          // update company industry
          let obj = {
            "industry": formData.Industry || ""
          };
          await dispatch(UpdateCompanyConfig(companyId, obj, setLoading)).then(() => {
            handleNext();
          });
        } else if (activeStep === 2){
          // update company payment rails
          let obj = {
            "domestic": formData.DomesticRail || "",
            "international": formData.GlobalRail || ""
          };
          await dispatch(UpdateCompanyConfig(companyId, obj, setLoading)).then(() => {
            handleClose();
          });
        }
      } else {
        toast.error("Something went wrong");
        handleClose()
      }
    } catch (err){
      console.log(err)
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box
        className="fullpage__modal animate__animated animate__fadeIn">
        
        <ModalTopBar 
          onBack={handleClose}
          title={""}
          />

        <Container 
          maxWidth="sm">
          <LinearProgress
            sx={{ width: "100%", mb: "20px" }}
            variant="determinate"
            value={ (activeStep + 1) * 25 } 
            />
        </Container>
        {
          activeStep === 0
          ?
          // Basic detials
          <Container 
            maxWidth="sm">
            <Typography
              mt={"20px"}
              mb={"10px"}
              variant='h6'
              textAlign={"center"}>
              Details for new company
            </Typography>
            <Box
              className="flex__Column"
              gap={"15px"}
              pt="20px"
              pb={"30px"}>
              <TextField 
                label="Company name *"
                fullWidth
                name="LegalName"
                value={formData.LegalName}
                onChange={handleChange}
                inputProps={{
                  maxLength: 128
                }}
                />
              <Box
                className="flexCenter_Row"
                gap="15px">
                <TextField 
                  select
                  data-testid="company-type-select"
                  label="Company type *"
                  fullWidth
                  name="EntityType"
                  value={formData.EntityType}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  >
                  {
                    entityTypeList.map((entity, entityIndex) => (
                      <MenuItem
                        key={entityIndex}
                        value={entity.value}>
                        <Typography>
                          {entity.label}
                        </Typography>
                      </MenuItem>
                    ))
                  }
                </TextField>
              </Box>
              {/* <Box
                className="flexCenter_Row"
                gap="15px">
                <TextField 
                  label="Contact first name *"
                  fullWidth
                  name="ContactFirstName"
                  value={formData.ContactFirstName}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  />
                <TextField 
                  label="Contact last name *"
                  fullWidth
                  name="ContactLastName"
                  value={formData.ContactLastName}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  />
              </Box> */}
              {/* <Box
                className="flexCenter_Row"
                gap={"15px"}>
                <TextField 
                  label="Contact email *"
                  fullWidth
                  name="Email"
                  value={formData.Email}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  />
                {
                  (formData.EntityType === "sole_proprietorship" ||
                  formData.EntityType === "unincorporated_association" || 
                  formData.EntityType === "trust")
                  && 
                  <TextField
                    label="Date of Birth *"
                    fullWidth
                    type="date"
                    name="ContactPersonDOB"
                    value={formData.ContactPersonDOB}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                }
              </Box> */}

              <Typography
                mt={"20px"}
                mb={"10px"}
                variant='substile1'>
                Contact Person
              </Typography>
              <Box
                className="flexCenter_Row"
                gap="15px">
                <TextField 
                  label="Contact first name *"
                  fullWidth
                  name="ContactFirstName"
                  value={formData.ContactFirstName}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  />
                <TextField 
                  label="Contact last name *"
                  fullWidth
                  name="ContactLastName"
                  value={formData.ContactLastName}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  />
              </Box>
              <Box
                className="flexCenter_Row"
                gap={"15px"}>
                <TextField 
                  label="Contact email *"
                  fullWidth
                  name="Email"
                  value={formData.Email}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  />
                {
                  (formData.EntityType === "sole_proprietorship" ||
                  formData.EntityType === "unincorporated_association" || 
                  formData.EntityType === "trust")
                  && 
                  <TextField
                    label="Date of Birth *"
                    fullWidth
                    type="date"
                    name="ContactPersonDOB"
                    value={formData.ContactPersonDOB}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                }
              </Box>
              <Typography
                mt={"20px"}
                mb={"10px"}
                variant='substile1'>
                Company Address
              </Typography>
              <TextField 
                label="Company address *"
                fullWidth
                name="Address"
                value={formData.Address}
                onChange={handleChange}
                inputProps={{
                  maxLength: 256
                }}
                />
              <Box
                className="flexCenter_Row"
                gap="15px">
                <TextField 
                  label="City *"
                  fullWidth
                  name="City"
                  value={formData.City}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  />
                <TextField 
                  select
                  label="State *"
                  data-testid="state-select"
                  fullWidth
                  name="State"
                  value={formData.State}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  >
                  {
                    stateList.map((state, stateIndex) => (
                      <MenuItem
                        key={stateIndex}
                        value={state.code}>
                        <Typography>
                          {state.name}
                        </Typography>
                      </MenuItem>
                    ))
                  }
                </TextField>
              </Box>
              <Box
                className="flexCenter_Row"
                gap="15px">
                <TextField 
                  label="Country *"
                  fullWidth
                  name="Country"
                  disabled
                  value={formData.Country}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 128
                  }}
                  />
                <TextField 
                  label="Postal code *"
                  fullWidth
                  type='number'
                  name="Pincode"
                  value={formData.Pincode}
                  onChange={handleChange}
                  />
              </Box>

              <Typography
                mt={"20px"}
                mb={"10px"}
                variant='substile1'>
                Tax Information
              </Typography>

              <Box
                className="flexCenter_Row"
                gap="15px">
                <TextField 
                  select
                  data-testid="taxid-type-select"
                  label="Tax Id Type *"
                  fullWidth
                  name="TaxIdType"
                  value={formData.TaxIdType}
                  onChange={handleChange}>
                  {
                    taxIdTypeList.map((taxIdType, taxIdTypeIndex) => (
                      <MenuItem
                        key={taxIdTypeIndex}
                        value={taxIdType.value}>
                        <Typography>
                          {taxIdType.label}
                        </Typography>
                      </MenuItem>
                    ))
                  }
                </TextField>
                <TextField 
                  label="Tax Id *"
                  fullWidth
                  type='number'
                  name="TaxId"
                  value={formData.TaxId}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                    handleChange({
                      target: {
                        name: e.target.name,
                        value: value
                      }
                    });
                  }}
                  inputProps={{
                    maxLength: 9
                  }}
                  />
              </Box>
              <Box
                className="flexCenter_Row"
                gap="15px">
                <Box
                  width={"50%"}>
                  <LocalizationProvider 
                    dateAdapter={AdapterDayjs}>
                    <DatePicker
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          helperText: 
                            dayjs.tz.guess() !== "America/New_York" &&
                            <span style={{ fontSize: "11px" }}>
                              Calender showned in EST timezone
                            </span>,
                          // width: "400px"
                        } 
                      }}
                      label="Incorporation date" 
                      name="IncorporationDate"
                      value={formData.IncorporationDate || null}
                      maxDate={dayjs()}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "IncorporationDate",
                            value: e
                          }
                        });
                      }}/>
                  </LocalizationProvider>
                </Box>
                <Box  
                  width={"50%"}>
                </Box>
              </Box>
              <Box
                className="flexCenter_Row"
                gap={"15px"}
                mt={"20px"}>
                <TextField 
                  label="Company website"
                  fullWidth
                  name="CompanyWebsite"
                  value={formData.CompanyWebsite}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 256
                  }}
                  />
                <TextField 
                  label="Estimated/Actual annual revenue"
                  fullWidth
                  name="EstimateAnnualRevenue"
                  value={formData.EstimateAnnualRevenue}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 256
                  }}
                  />
              </Box>
            </Box>
          </Container>
          : 
            activeStep === 1 
            ? 
            // Industry
            <Container 
              maxWidth="sm">
              <Typography
                mt={"20px"}
                mb={"40px"}
                variant='h6'
                textAlign={"center"}>
                Select Company Industry
              </Typography>
              <Box className="flex__Column" 
                gap={"15px"} 
                pt="20px"
                pb="30px">
                <Box
                  className="flexCenter_Row"
                  gap="15px">
                  <Box
                    onClick={() => handleChange({ target: { name: "Industry", value: "CONSTRUCTION" } })}
                    className={`industry-box ${formData.Industry === "CONSTRUCTION" ? "selected" : ""}`}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight={600}>
                      Construction
                    </Typography> 
                  </Box>
                  <Box
                    onClick={() => handleChange({ target: { name: "Industry", value: "IMPORT_EXPORT" } })}
                    className={`industry-box ${formData.Industry === "IMPORT_EXPORT" ? "selected" : ""}`}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight={600}>
                      Import Export
                    </Typography> 
                  </Box>
                </Box>
                <Box
                  className="flexCenter_Row"
                  gap="15px">
                  <Box
                    onClick={() => handleChange({ target: { name: "Industry", value: "FREIGHT" } })}
                    className={`industry-box ${formData.Industry === "FREIGHT" ? "selected" : ""}`}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight={600}>
                      Freight
                    </Typography> 
                  </Box>
                  <Box
                    onClick={() => handleChange({ target: { name: "Industry", value: "GENERIC" } })}
                    className={`industry-box ${formData.Industry === "GENERIC" ? "selected" : ""}`}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight={600}>
                      Generic
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Container> 
            : 
              activeStep === 2
              ? 
              // Payment rails
              <Container 
                maxWidth="sm">
                <Typography
                  mt={"20px"}
                  mb={"40px"}
                  variant='h6'
                  textAlign={"center"}>
                  Select Payment Rails
                </Typography>
                <Box className="flex__Column" 
                  gap={"15px"} 
                  pt="20px"
                  pb="30px">
                  <Box
                    className="flexCenter_Row"
                    gap="15px">
                    <Box
                      onClick={() => handleChange({ target: { name: "DomesticRail", value: !formData.DomesticRail } })}
                      className={`flexCenterCenterColumn
                        industry-box ${formData.DomesticRail ? "selected" : ""}`}>
                      <OutlinedFlagIcon
                        fontSize='large' 
                        />
                      <Typography 
                        variant="h6" 
                        fontWeight={600}>
                        Domestic
                      </Typography> 
                      <Typography
                        variant="caption"
                        color={"grey"}>
                        Send or receive payments within the US
                      </Typography>
                    </Box>
                    <Box
                      onClick={() => handleChange({ target: { name: "GlobalRail", value: !formData.GlobalRail } })}
                      className={`flexCenterCenterColumn
                        industry-box ${formData.GlobalRail ? "selected" : ""}`}>
                      <PublicIcon
                        fontSize='large' 
                        />
                      <Typography 
                        variant="h6" 
                        fontWeight={600}>
                        Global
                      </Typography> 
                      <Typography
                        variant="caption"
                        color={"grey"}>
                        Send or receive payments globally
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Container> 
              : 
                <></>
        }
        <Container 
          maxWidth="sm">
          <Box 
            className="flexCenterFERow" 
            gap={"15px"}
            mt={"20px"}
            mb={"40px"}>
            <LoadingButton
              variant="contained"
              disabled={
                activeStep === 0
                ? !isStep0Valid()
                : activeStep === 1
                  ? formData.Industry === ""
                  : activeStep === 2
                    ? !(formData.DomesticRail || formData.GlobalRail)
                    : true
              }
              onClick={() => handleSubmit()}
              loading={loading}>
                Next
            </LoadingButton>
          </Box>
        </Container>
      </Box>
    </Modal>
  )
}

export default CompaniesModal;